import { Badge, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useMemo } from "react";
import { visualCard, visualHead, visualMeta, visualTitle } from "@/components/visual/visual-styles";
import { contentColumn, filterScroll, pageMax, pageStackAt } from "@/shell/content-frame";
import type { Project, UserResource } from "@/engine/types";
import { fmtCurrency } from "@/lib/format";
import { formatDate } from "@/lib/date-format";
import { useMoneyMasked } from "@/features/access/use-plan-access";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { buildReportContext, type ChangeValue, type ReportContext, type ReportLevel, type ReportMetrics, type ReportPeriod } from "./report-model";

/* ------------------------------------------------------------------------------------------------
 * Comparison points: the live plan, closed periods and (compare-only) checkpoints.
 * ---------------------------------------------------------------------------------------------- */

export type PointKind = "current" | "period" | "checkpoint";

export interface ReportPoint {
  key: string;
  kind: PointKind;
  label: string;
  sub: string;
  project: Project;
  period?: ReportPeriod;
  checkpointId?: string;
}

export const CURRENT_KEY = "current";

const cache = new WeakMap<Project, WeakMap<Record<string, UserResource>, ReportContext>>();

export function contextFor(project: Project, resources: Record<string, UserResource>, frozen?: ReportMetrics): ReportContext {
  let inner = cache.get(project);
  if (!inner) {
    inner = new WeakMap();
    cache.set(project, inner);
  }
  const hit = inner.get(resources);
  if (hit) return hit;
  const ctx = buildReportContext(project, resources, frozen);
  inner.set(resources, ctx);
  return ctx;
}

export function useReportPoints() {
  const project = useProjectStore((s) => s.project);
  const reports = useProjectStore((s) => s.reports);
  const checkpoints = useProjectStore((s) => s.checkpoints);
  const resources = useCatalogStore((s) => s.resources);

  return useMemo(() => {
    const current: ReportPoint = { key: CURRENT_KEY, kind: "current", label: "현재 (작성 중)", sub: `진척 기준일 ${project.statusDate}`, project };
    const periods: ReportPoint[] = reports.map((r) => ({ key: `rp:${r.id}`, kind: "period", label: r.name, sub: `마감 ${formatDate(r.closedAt, "dateTime")} · 기준일 ${r.metrics.statusDate}`, project: r.project, period: r }));
    const cps: ReportPoint[] = checkpoints.map((c) => ({ key: `cp:${c.id}`, kind: "checkpoint", label: c.name, sub: `체크포인트 · ${formatDate(c.createdAt, "dateTime")}`, project: c.project, checkpointId: c.id }));
    const all = [current, ...periods, ...cps];
    const byKey = new Map(all.map((p) => [p.key, p]));
    const ctx = (key: string) => {
      const p = byKey.get(key);
      return p ? contextFor(p.project, resources, p.period?.metrics) : null;
    };
    /** the period closed just before this point (current → newest period) */
    const previousOf = (key: string): ReportPoint | null => {
      if (key === CURRENT_KEY) return periods[0] ?? null;
      const i = periods.findIndex((p) => p.key === key);
      return i >= 0 ? (periods[i + 1] ?? null) : null;
    };
    return { current, periods, checkpoints: cps, all, byKey, ctx, previousOf, resources };
  }, [project, reports, checkpoints, resources]);
}

export type ReportPoints = ReturnType<typeof useReportPoints>;

export interface ReportTrendRow {
  point: ReportPoint;
  m: ReportMetrics;
}

/** Current and closed report periods, newest first; checkpoints are compare-only. */
export function useTrendRows(points: ReportPoints): ReportTrendRow[] {
  return useMemo(
    () => [points.current, ...points.periods].map((point) => ({ point, m: points.ctx(point.key)!.metrics })),
    [points],
  );
}

export function useMaskedMoney() {
  const { masked } = useMoneyMasked();
  return masked;
}

export function fmtChangeValue(v: ChangeValue | undefined, masked: boolean, signed = false): string {
  if (v === undefined) return "—";
  if (typeof v === "string") return v;
  if (masked) return "₩ •••";
  if (!signed) return fmtCurrency(v.money);
  if (v.money === 0) return "±₩0";
  return `${v.money > 0 ? "+" : "−"}${fmtCurrency(Math.abs(v.money))}`;
}

export function fmtSignedPp(n: number): string {
  if (n === 0) return "±0%p";
  return `${n > 0 ? "+" : ""}${Math.round(n)}%p`;
}

export function fmtSignedNum(n: number, digits = 0, unit = ""): string {
  const v = Number(n.toFixed(digits));
  if (v === 0) return `±0${unit}`;
  return `${v > 0 ? "+" : ""}${v}${unit}`;
}

/* ------------------------------------------------------------------------------------------------
 * Shared chrome
 * ---------------------------------------------------------------------------------------------- */

export const useReportStyles = makeStyles({
  body: filterScroll,
  wrap: contentColumn,
  /** 요약·분석과 같은 Visual 크롬(@/components/visual/visual-styles) */
  card: visualCard,
  cardHead: visualHead,
  cardTitle: visualTitle,
  meta: visualMeta,
  secondary: { color: tokens.colorNeutralForeground2 },
  tertiary: { color: tokens.colorNeutralForeground3 },
  danger: { color: tokens.colorPaletteRedForeground1 },
  warning: { color: tokens.colorPaletteDarkOrangeForeground1 },
  success: { color: tokens.colorPaletteGreenForeground1 },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  truncate: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    [pageMax(pageStackAt)]: { gridTemplateColumns: "1fr" },
  },
  listRow: {
    display: "grid",
    gridTemplateColumns: "52px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minHeight: "30px",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    margin: `0 -${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: "none",
    background: "none",
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    width: `calc(100% + ${tokens.spacingHorizontalXS} * 2)`,
    boxSizing: "border-box",
  },
  listRowButton: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  code: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  levelDot: {
    width: "8px",
    height: "8px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  levelGood: { backgroundColor: tokens.colorPaletteGreenBackground3 },
  levelWatch: { backgroundColor: tokens.colorPaletteDarkOrangeBackground3 },
  levelRisk: { backgroundColor: tokens.colorPaletteRedBackground3 },
});

export function LevelDot({ level }: { level: ReportLevel }) {
  const s = useReportStyles();
  return <span aria-hidden className={mergeClasses(s.levelDot, level === "risk" ? s.levelRisk : level === "watch" ? s.levelWatch : s.levelGood)} />;
}

export function SourceBadge({ period }: { period?: ReportPeriod }) {
  if (!period || period.source === "manual") return null;
  return (
    <Badge appearance="tint" color="informative" size="small" shape="rounded">
      {period.source === "demo" ? "예시" : "체크포인트"}
    </Badge>
  );
}
